import React, { useState, useEffect } from "react";
import axios from "axios";


/* ─────────────────────────────────────────
   DEFAULT METHODOLOGY (Whole Exome Sequencing)
───────────────────────────────────────── */
const DEFAULT_METHODOLOGY = `Whole Exome Sequencing (WES) Workflow:

1. Genomic DNA Extraction, Library Preparation & Exome Target Enrichment:
High-molecular-weight genomic DNA was extracted and quantified using Qubit Fluorometric Quantitation. Genomic DNA was randomly sheared into fragments of 200–300 bp. Sequencing adapters were ligated, and exonic regions were selectively captured using biotinylated oligonucleotide probes designed against the human consensus exome. Enriched exome libraries were amplified by PCR and assessed on an Agilent Bioanalyzer / TapeStation.

2. High-Throughput Sequencing & Quality Control:
Paired-end sequencing (2 × 150 bp) was performed on Illumina high-throughput sequencing platforms. Raw FASTQ reads were quality-assessed using FastQC. Adapter contamination and low-quality bases (< Q20) were removed using TrimGalore.

3. Read Alignment & Preprocessing:
Cleaned paired-end reads were mapped to the reference genome (GRCh38 / hg38) using BWA-MEM with default parameters. PCR duplicates were identified and flagged using Picard MarkDuplicates. Base Quality Score Recalibration (BQSR) was applied using GATK to correct systematic machine errors.

4. Variant Discovery & Functional Annotation:
Germline Single Nucleotide Polymorphisms (SNPs) and Small Insertions/Deletions (InDels) were discovered simultaneously across samples using GATK HaplotypeCaller in GVCF mode, followed by joint genotyping. Strict filtering was applied based on read depth and quality score. Functional annotation and genomic impact predictions were performed using SnpEff against dbSNP, 1000 Genomes, ClinVar, and ExAC databases.`;

const DEFAULT_SOFTWARE = [
  { item: "Quality Assessment & Trimming", software: "FastQC / TrimGalore", version: "v0.12.1 / v0.6.10" },
  { item: "Exome Read Alignment", software: "BWA-MEM", version: "v0.7.17" },
  { item: "PCR Duplicate Removal", software: "Picard MarkDuplicates", version: "v2.27.5" },
  { item: "Variant Calling (SNPs & InDels)", software: "GATK HaplotypeCaller", version: "v4.4.0.0" },
  { item: "Variant Functional Annotation", software: "SnpEff / dbSNP", version: "v5.1d" },
  { item: "Clinical Reference Databases", software: "ClinVar / 1000G / ExAC", version: "Latest Build" },
];

/* ─────────────────────────────────────────
   DYNAMIC THEME STYLES (Matching Bacterial WGS)
───────────────────────────────────────── */
function getStyle(dark) {
  const bg = dark ? '#0a1628' : '#f4f6f8';
  const cardBg = dark ? '#0f2040' : '#fff';
  const cardBorder = dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb';
  const text = dark ? '#e8edf4' : '#111827';
  const textSub = dark ? '#8aaac8' : '#6b7280';
  const inputBg = dark ? '#162a50' : '#fff';
  const inputBorder = dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb';
  const dropBg = dark ? '#162a50' : '#faf5ff';
  const dropHoverBg = dark ? '#1e3460' : '#f3e8ff';
  const dropBorder = dark ? 'rgba(168,85,247,0.3)' : '#e9d5ff';
  const dropBorderHover = '#a855f7';
  const accent = '#a855f7';
  const accentDark = '#9333ea';
  const accentLight = dark ? 'rgba(168,85,247,0.12)' : '#f3e8ff';
  const accentText = dark ? '#d8b4fe' : '#7e22ce';

  return `
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');
.wes-page *, .wes-page *::before, .wes-page *::after { box-sizing: border-box; }
.wes-page { font-family:'Barlow',sans-serif; background:${bg}; min-height:100vh; padding:0 0 90px; color:${text}; }
.wes-page .wes-breadcrumb { display:flex; align-items:center; gap:6px; padding:14px 32px; font-size:13px; color:${textSub}; border-bottom:1px solid ${cardBorder}; background:${cardBg}; }
.wes-page .wes-breadcrumb .bc-active { color:${accent}; font-weight:600; }
.wes-page .wes-breadcrumb .bc-sep { color:${dark ? '#4a6582' : '#d1d5db'}; }
.wes-page .wes-content { max-width:980px; margin:0 auto; padding:28px 16px; display:flex; flex-direction:column; gap:20px; }
.wes-page .section-card { background:${cardBg}; border-radius:12px; border:1px solid ${cardBorder}; padding:28px 32px; box-shadow:0 1px 4px rgba(0,0,0,0.06); }
.wes-page .section-header { display:flex; align-items:flex-start; gap:14px; margin-bottom:24px; }
.wes-page .section-header-text h3 { margin:0 0 3px; font-size:19px; font-weight:800; color:${text}; }
.wes-page .section-header-text p { margin:0; font-size:13px; color:${textSub}; }
.wes-page .divider { height:1px; background:${dark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'}; margin:20px 0; }
.wes-page .sub-label { font-size:13px; font-weight:800; text-transform:uppercase; letter-spacing:.8px; color:${accent}; margin-bottom:12px; padding-bottom:6px; border-bottom:1.5px solid ${dark ? 'rgba(192,132,252,0.2)' : '#f3e8ff'}; }
.wes-page .g2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.wes-page .g3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; }
@media(max-width:768px){ .wes-page .g2, .wes-page .g3 { grid-template-columns:1fr; } }
.wes-page .field { display:flex; flex-direction:column; gap:5px; }
.wes-page .field > label { font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; color:${dark ? '#cbd5e1' : '#374151'}; }
.wes-page .field > label .req { color:#dc2626; margin-left:2px; }
.wes-page .field input { padding:10px 14px; border:1.5px solid ${inputBorder}; border-radius:8px; font-size:14px; font-family:'Barlow',sans-serif; color:${text}; background:${inputBg}; outline:none; width:100%; transition:border-color .18s, box-shadow .18s; }
.wes-page .field input:focus { border-color:${accent}; box-shadow:0 0 0 3px rgba(192,132,252,.2); }
.wes-page .field input::placeholder { color:${dark ? '#4a6582' : '#c9cdd4'}; }

.wes-page .drop-area { border:2px dashed ${dropBorder}; border-radius:10px; padding:20px 16px; text-align:center; cursor:pointer; background:${dropBg}; position:relative; transition:all .18s; }
.wes-page .drop-area:hover, .wes-page .drop-area.drag { border-color:${dropBorderHover}; background:${dropHoverBg}; }
.wes-page .drop-area input[type=file] { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
.wes-page .drop-main { font-size:13px; font-weight:600; color:${accentText}; margin-top:6px; }
.wes-page .drop-hint { font-size:11px; color:${dark ? '#8aaac8' : '#9ca3af'}; margin-top:2px; }

.wes-page .chip-list { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
.wes-page .chip { display:inline-flex; align-items:center; gap:5px; background:${accentLight}; color:${accentText}; border:1px solid ${dropBorder}; border-radius:20px; padding:3px 10px 3px 8px; font-size:11px; font-weight:500; max-width:260px; }
.wes-page .chip span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.wes-page .chip button { background:none; border:none; cursor:pointer; color:${accentText}; font-size:13px; line-height:1; padding:0; margin-left:2px; }

.wes-page .info-banner { display:flex; align-items:flex-start; gap:10px; background:${accentLight}; border:1px solid ${dropBorder}; border-radius:8px; padding:12px 14px; margin-bottom:16px; }
.wes-page .info-banner-icon { font-size:15px; flex-shrink:0; margin-top:1px; }
.wes-page .info-banner-text { font-size:12px; color:${accentText}; line-height:1.6; }
.wes-page .info-banner-text strong { font-weight:700; }

.wes-page .software-table { width:100%; border-collapse:collapse; margin-top:10px; }
.wes-page .software-table th { text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; color:${dark ? '#cbd5e1' : '#374151'}; padding:8px; border-bottom:1.5px solid ${cardBorder}; }
.wes-page .software-table td { padding:6px 8px; vertical-align:middle; }
.wes-page .software-table input { padding:8px 10px; border:1.5px solid ${inputBorder}; border-radius:6px; font-size:13px; outline:none; width:100%; font-family:'Barlow',sans-serif; background:${inputBg}; color:${text}; }
.wes-page .software-table input:focus { border-color:${accent}; box-shadow:0 0 0 2px rgba(192,132,252,.2); }
.wes-page .btn-add-step { background:${accentLight}; color:${accentText}; border:1px solid ${dropBorder}; border-radius:6px; padding:6px 14px; font-size:12px; font-weight:600; cursor:pointer; margin-top:10px; transition:all .15s; }
.wes-page .btn-add-step:hover { background:${dark ? 'rgba(192,132,252,0.2)' : '#e9d5ff'}; }
.wes-page .btn-remove-step { background:none; border:none; color:#dc2626; cursor:pointer; font-size:14px; padding:4px; border-radius:4px; }
.wes-page .btn-remove-step:hover { background:${dark ? 'rgba(220,38,38,0.15)' : '#fee2e2'}; }

.wes-page .method-area { width:100%; min-height:240px; resize:vertical; border:1.5px solid ${inputBorder}; border-radius:10px; padding:14px 16px; font-size:14px; line-height:1.85; color:${text}; font-family:'Barlow',sans-serif; background:${dark ? '#0c1a30' : '#fafbfd'}; outline:none; box-sizing:border-box; }
.wes-page .method-area:focus { border-color:${accent}; box-shadow:0 0 0 3px rgba(192,132,252,.2); }
.wes-page .method-meta { display:flex; align-items:center; justify-content:space-between; margin-top:7px; padding:0 2px; }
.wes-page .method-word-count { font-size:11px; color:${textSub}; font-variant-numeric:tabular-nums; }
.wes-page .method-clear { background:none; border:none; cursor:pointer; font-size:11px; color:#dc2626; font-weight:700; padding:2px 6px; border-radius:5px; }
.wes-page .method-clear:hover { background:${dark ? 'rgba(220,38,38,0.15)' : '#fee2e2'}; }

.wes-submit-bar { position:fixed; bottom:0; left:0; right:0; background:${cardBg}; border-top:1px solid ${cardBorder}; padding:14px 32px; display:flex; align-items:center; justify-content:flex-end; gap:12px; box-shadow:0 -2px 12px rgba(0,0,0,0.07); z-index:100; }
.wes-btn-submit { background:${accent}; color:#fff; border:none; border-radius:8px; padding:12px 36px; font-size:14px; font-weight:600; font-family:'Barlow',sans-serif; cursor:pointer; display:flex; align-items:center; gap:8px; transition:background .18s, transform .15s; }
.wes-btn-submit:hover:not(:disabled) { background:${accentDark}; transform:translateY(-1px); }
.wes-btn-submit:disabled { opacity:0.7; cursor:not-allowed; }
.wes-submit-error { font-size:12px; font-weight:600; color:#dc2626; background:${dark ? 'rgba(220,38,38,0.15)' : '#fee2e2'}; border:1px solid #fca5a5; border-radius:7px; padding:7px 12px; }
.wes-submit-success { font-size:12px; font-weight:600; color:#15803d; background:${dark ? 'rgba(34,197,94,0.15)' : '#f0fdf4'}; border:1px solid #86efac; border-radius:7px; padding:7px 12px; }

@keyframes wes-spin { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }
`;
}

function injectStyle(dark) {
  let s = document.getElementById("wes-report-style");
  if (!s) {
    s = document.createElement("style");
    s.id = "wes-report-style";
    document.head.appendChild(s);
  }
  s.textContent = getStyle(dark);
}

/* ─────────────────────────────────────────
   DROP AREA COMPONENT (Identical to Bacterial WGS)
───────────────────────────────────────── */
function DropArea({ accept, mainLabel, subLabel, icon = "📁", file, onFile, isMulti = false, files = [], onFiles }) {
  const [drag, setDrag] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (isMulti) {
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length && onFiles) onFiles([...files, ...dropped]);
    } else {
      const f = e.dataTransfer.files[0];
      if (f && onFile) onFile(f);
    }
  };

  const handleChange = (e) => {
    if (isMulti) {
      const selected = Array.from(e.target.files);
      if (selected.length && onFiles) onFiles([...files, ...selected]);
    } else {
      const f = e.target.files[0];
      if (f && onFile) onFile(f);
    }
  };

  return (
    <div
      className={`drop-area ${drag ? "drag" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
    >
      <input type="file" accept={accept} multiple={isMulti} onChange={handleChange} />
      <div style={{ fontSize: 24, lineHeight: 1 }}>{file || (isMulti && files.length > 0) ? "✅" : icon}</div>
      {isMulti ? (
        files.length > 0 ? (
          <div className="drop-main">{files.length} file(s) selected</div>
        ) : (
          <>
            <div className="drop-main">{mainLabel}</div>
            <div className="drop-hint">{subLabel}</div>
          </>
        )
      ) : file ? (
        <>
          <div className="drop-main">{file.name}</div>
          <div className="drop-hint">{(file.size / 1024).toFixed(1)} KB</div>
        </>
      ) : (
        <>
          <div className="drop-main">{mainLabel}</div>
          <div className="drop-hint">{subLabel}</div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN WES REPORT UPLOAD COMPONENT
───────────────────────────────────────── */
export default function WesReportUpload({ setReportUrls, setActiveTab, dark }) {
  useEffect(() => {
    injectStyle(dark);
    return () => {
      const s = document.getElementById("wes-report-style");
      if (s) s.remove();
    };
  }, [dark]);

  // Form states
  const [ci, setCi] = useState({
    reportTitle: "Whole Exome Sequencing QC Report",
    clientName: "",
    projectId: "",
    institution: "",
    contact: "",
    salesperson: "",
    numberOfSamples: "",
    species: "Homo sapiens (GRCh38 / hg38)",
    minDP: "50",
    minQ: "100",
  });

  const [softwareRows, setSoftwareRows] = useState(DEFAULT_SOFTWARE.map(r => ({ ...r })));
  const [methodologyText, setMethodologyText] = useState(DEFAULT_METHODOLOGY);

  // File states
  const [rawSequence, setRawSequence] = useState(null);
  const [rawMultiqc, setRawMultiqc] = useState(null);
  const [rawQuality, setRawQuality] = useState(null);
  const [qualityPlot, setQualityPlot] = useState(null);
  const [gcPlot, setGcPlot] = useState(null);
  const [extraPlot, setExtraPlot] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const [trimmedSequence, setTrimmedSequence] = useState(null);
  const [trimmedMultiqc, setTrimmedMultiqc] = useState(null);
  const [trimmedQuality, setTrimmedQuality] = useState(null);
  const [trimmedQualityPlot, setTrimmedQualityPlot] = useState(null);
  const [trimmedGcPlot, setTrimmedGcPlot] = useState(null);

  const [genomeZips, setGenomeZips] = useState([]);
  const [variantsStats, setVariantsStats] = useState(null);
  const [snpeffHtmls, setSnpeffHtmls] = useState([]);
  const [resourcesZip, setResourcesZip] = useState(null);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [downloadedReport, setDownloadedReport] = useState(null);

  const handleCi = (e) => setCi(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const addSoftwareRow = () => setSoftwareRows(prev => [...prev, { item: "", software: "", version: "" }]);
  const removeSoftwareRow = (i) => setSoftwareRows(prev => prev.filter((_, idx) => idx !== i));
  const editSoftwareRow = (i, key, val) => setSoftwareRows(prev => prev.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

  const wordCount = methodologyText.trim() ? methodologyText.trim().split(/\s+/).filter(Boolean).length : 0;

  const triggerHtmlDownload = async (reportUrl, filename) => {
    try {
      const fileRes = await fetch(reportUrl);
      if (fileRes.ok) {
        const htmlBlob = await fileRes.blob();
        const blobUrl = window.URL.createObjectURL(htmlBlob);
        const dlLink = document.createElement("a");
        dlLink.href = blobUrl;
        dlLink.download = filename;
        document.body.appendChild(dlLink);
        dlLink.click();
        setTimeout(() => {
          document.body.removeChild(dlLink);
          window.URL.revokeObjectURL(blobUrl);
        }, 1000);
        return;
      }
    } catch (e) {
      console.warn("Direct blob download error, falling back:", e);
    }
    const dlLink = document.createElement("a");
    dlLink.href = `/api/wes/download-report?file=${encodeURIComponent(filename)}`;
    dlLink.download = filename;
    document.body.appendChild(dlLink);
    dlLink.click();
    setTimeout(() => document.body.removeChild(dlLink), 500);
  };

  const handleUpload = async () => {
    setError("");
    setSuccess(false);

    const fd = new FormData();
    fd.append("clientInfo", JSON.stringify(ci));
    fd.append("methodology", methodologyText);
    fd.append("softwareSteps", JSON.stringify(softwareRows.map(s => ({
      step: s.item,
      tool: s.software,
      version: s.version
    }))));

    // Raw FastQC files
    if (rawSequence) fd.append("raw_sequence", rawSequence);
    if (rawMultiqc) fd.append("raw_multiqc", rawMultiqc);
    if (rawQuality) fd.append("raw_quality", rawQuality);
    if (qualityPlot) fd.append("quality_plot", qualityPlot);
    if (gcPlot) fd.append("gc_plot", gcPlot);
    if (extraPlot) fd.append("extra_plot", extraPlot);
    if (logoFile) fd.append("logo", logoFile);

    // Trimmed FastQC files
    if (trimmedSequence) fd.append("trimmed_sequence", trimmedSequence);
    if (trimmedMultiqc) fd.append("trimmed_multiqc", trimmedMultiqc);
    if (trimmedQuality) fd.append("trimmed_quality", trimmedQuality);
    if (trimmedQualityPlot) fd.append("trimmed_quality_plot", trimmedQualityPlot);
    if (trimmedGcPlot) fd.append("trimmed_gc_plot", trimmedGcPlot);

    // Mapping & Variant files
    genomeZips.forEach((zipFile) => {
      fd.append("genome_zips", zipFile);
    });

    if (variantsStats) fd.append("variants_stats", variantsStats);

    snpeffHtmls.forEach((htmlFile) => {
      fd.append("snpeff_htmls", htmlFile);
    });

    if (resourcesZip) fd.append("resourcesZip", resourcesZip);

    setLoading(true);

    try {
     const res = await axios.post("/api/wes/generate-report", fd, {
             headers: { "Content-Type": "multipart/form-data" }
           });

      const data = res.data;
      setLoading(false);

      if (!data.success) {
        throw new Error(data.error || "Failed to generate WES report.");
      }

       const downloadFilename = data.filename || `wes_report_${Date.now()}.html`;
      const fullReportUrl = data.reportUrl;

      // Automatically trigger HTML report file download
      await triggerHtmlDownload(fullReportUrl, downloadFilename);

      // Open in new tab
      window.open(fullReportUrl, "_blank");

      setDownloadedReport({
        filename: downloadFilename,
        reportUrl: fullReportUrl,
        downloadUrl: data.downloadUrl || `/api/wes/download-report?file=${encodeURIComponent(downloadFilename)}`
      });

      setSuccess(true);

      if (setReportUrls) {
        setReportUrls({
          html: data.reportUrl,
          qcExcel: data.excelUrl,
          mappingExcel: data.mappingExcelUrl,
          variantExcel: data.variantExcelUrl
        });
      }

    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || err.message || "Network error while connecting to server.");
    }
  };

  return (
    <div className="wes-page">
      {/* Top Breadcrumb Navigation */}
      <div className="wes-breadcrumb">
        <span>Whole Exome Sequencing (WES)</span>
        <span className="bc-sep">›</span>
        <span className="bc-active">Upload Files &amp; Generate Report</span>
      </div>

      <div className="wes-content">
        {/* Section 1: Client & Project Info */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Client &amp; Project Information</h3>
              <p>Enter project metadata, sample details, and analysis thresholds for the WES report</p>
            </div>
          </div>
          <div className="sub-label">General Metadata</div>
          <div className="g2">
            <div className="field">
              <label>Report Title</label>
              <input name="reportTitle" value={ci.reportTitle} onChange={handleCi} placeholder="e.g. Whole Exome Sequencing QC Report" />
            </div>
            <div className="field">
              <label>Client Name <span className="req">*</span></label>
              <input name="clientName" value={ci.clientName} onChange={handleCi} placeholder="Dr. Rajesh Kumar" />
            </div>
            <div className="field">
              <label>Project ID <span className="req">*</span></label>
              <input name="projectId" value={ci.projectId} onChange={handleCi} placeholder="e.g. OPL_WES_6_34567" />
            </div>
            <div className="field">
              <label>Institution / Company</label>
              <input name="institution" value={ci.institution} onChange={handleCi} placeholder="AIIMS / Research Institute" />
            </div>
            <div className="field">
              <label>Contact Email</label>
              <input name="contact" value={ci.contact} onChange={handleCi} placeholder="client@institute.edu" />
            </div>
            <div className="field">
              <label>Salesperson</label>
              <input name="salesperson" value={ci.salesperson} onChange={handleCi} placeholder="Gowri Shankar J" />
            </div>
            <div className="field">
              <label>Number of Samples</label>
              <input name="numberOfSamples" value={ci.numberOfSamples} onChange={handleCi} placeholder="e.g. 6" />
            </div>
            <div className="field">
              <label>Species / Reference Genome</label>
              <input name="species" value={ci.species} onChange={handleCi} placeholder="Homo sapiens (GRCh38 / hg38)" />
            </div>
          </div>

          <div className="divider" />

          <div className="sub-label">Exome Variant Filtering Thresholds</div>
          <div className="g2">
            <div className="field">
              <label>Min Read Depth (DP) Filter</label>
              <input name="minDP" value={ci.minDP} onChange={handleCi} placeholder="50" />
            </div>
            <div className="field">
              <label>Min Quality (Q) Filter</label>
              <input name="minQ" value={ci.minQ} onChange={handleCi} placeholder="100" />
            </div>
          </div>
        </div>

        {/* Section 2: Raw FastQC Data */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Raw Data Quality Control (Data QC Tab)</h3>
              <p>Upload raw sequencing FastQC text metrics (.txt) and quality plot images (PNG/JPG)</p>
            </div>
          </div>

          <div className="info-banner">
            <span className="info-banner-icon">ℹ️</span>
            <span className="info-banner-text">
              <strong>Raw FASTQ Evaluation:</strong> Upload the summary text files generated by FastQC / MultiQC along with the per-base quality and GC content assessment plots.
            </span>
          </div>

          <div className="sub-label">Raw Sequencing FastQC Text Metrics</div>
          <div className="g3">
            <div className="field">
              <label>Sequence Counts (.txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={rawSequence}
                mainLabel="Upload Sequence Counts"
                subLabel="Text file with raw read counts"
                icon="📊"
                onFile={setRawSequence}
              />
            </div>
            <div className="field">
              <label>MultiQC FastQC (.txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={rawMultiqc}
                mainLabel="Upload MultiQC Stats"
                subLabel="MultiQC fastqc general metrics"
                icon="📋"
                onFile={setRawMultiqc}
              />
            </div>
            <div className="field">
              <label>Quality Scores (.txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={rawQuality}
                mainLabel="Upload Quality Scores"
                subLabel="Per-sample Phred score counts"
                icon="📈"
                onFile={setRawQuality}
              />
            </div>
          </div>

          <div className="divider" />

          <div className="sub-label">Raw FastQC Quality Assessment Plots</div>
          <div className="g3">
            <div className="field">
              <label>1. Per Base Quality Plot</label>
              <DropArea
                accept="image/*"
                file={qualityPlot}
                mainLabel="Upload Quality Plot"
                subLabel="Per base sequence quality plot"
                icon="🖼️"
                onFile={setQualityPlot}
              />
            </div>
            <div className="field">
              <label>2. Per Sequence GC Plot</label>
              <DropArea
                accept="image/*"
                file={gcPlot}
                mainLabel="Upload GC Plot"
                subLabel="Per sequence GC content distribution"
                icon="🖼️"
                onFile={setGcPlot}
              />
            </div>
            <div className="field">
              <label>3. Additional Plot (Optional)</label>
              <DropArea
                accept="image/*"
                file={extraPlot}
                mainLabel="Upload Extra Plot"
                subLabel="Additional FastQC assessment figure"
                icon="🖼️"
                onFile={setExtraPlot}
              />
            </div>
          </div>
        </div>

        {/* Section 3: Trimmed / Clean Data Quality Control */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Trimmed Data Quality Control (Trimmed FastQC Tab)</h3>
              <p>Upload trimmed clean read metrics and plots after adapter and low-quality base filtering</p>
            </div>
          </div>

          <div className="sub-label">Trimmed Clean Reads Metrics (.txt)</div>
          <div className="g3">
            <div className="field">
              <label>Trimmed Sequence Counts</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={trimmedSequence}
                mainLabel="Upload Trimmed Counts"
                subLabel="TrimGalore filtered read counts"
                icon="📊"
                onFile={setTrimmedSequence}
              />
            </div>
            <div className="field">
              <label>Trimmed MultiQC (.txt)</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={trimmedMultiqc}
                mainLabel="Upload Trimmed MultiQC"
                subLabel="Filtered multiqc summary stats"
                icon="📋"
                onFile={setTrimmedMultiqc}
              />
            </div>
            <div className="field">
              <label>Trimmed Quality Scores</label>
              <DropArea
                accept=".tsv,.csv,.txt"
                file={trimmedQuality}
                mainLabel="Upload Trimmed Scores"
                subLabel="Clean read quality score counts"
                icon="📈"
                onFile={setTrimmedQuality}
              />
            </div>
          </div>

          <div className="divider" />

          <div className="sub-label">Trimmed FastQC Assessment Plots</div>
          <div className="g2">
            <div className="field">
              <label>1. Trimmed Per Base Quality Plot</label>
              <DropArea
                accept="image/*"
                file={trimmedQualityPlot}
                mainLabel="Upload Clean Quality Plot"
                subLabel="Filtered per base sequence quality"
                icon="🖼️"
                onFile={setTrimmedQualityPlot}
              />
            </div>
            <div className="field">
              <label>2. Trimmed GC Content Plot</label>
              <DropArea
                accept="image/*"
                file={trimmedGcPlot}
                mainLabel="Upload Clean GC Plot"
                subLabel="Filtered GC content distribution"
                icon="🖼️"
                onFile={setTrimmedGcPlot}
              />
            </div>
          </div>
        </div>

        {/* Section 4: Exome Alignment & Variant Calling */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Exome Alignment, Variant Calling &amp; Annotations</h3>
              <p>Upload mapping statistics ZIPs, GATK variant summary tables, and SnpEff annotation HTML files</p>
            </div>
          </div>

          <div className="sub-label">Genome Alignment Mapping Archives (.ZIP)</div>
          <DropArea
            accept=".zip"
            isMulti={true}
            files={genomeZips}
            mainLabel="Upload Mapping ZIP Archives"
            subLabel="ZIP files containing genome_results.txt"
            icon="📦"
            onFiles={setGenomeZips}
          />
          {genomeZips.length > 0 && (
            <div className="chip-list">
              {genomeZips.map((z, i) => (
                <div key={i} className="chip">
                  <span>📦 {z.name}</span>
                  <button type="button" onClick={() => setGenomeZips(genomeZips.filter((_, idx) => idx !== i))}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="divider" />

          <div className="sub-label">Variant Statistics &amp; Functional Annotations</div>
          <div className="g2">
            <div className="field">
              <label>Variant Statistics File (.txt)</label>
              <DropArea
                accept=".txt,.tsv,.csv"
                file={variantsStats}
                mainLabel="Upload Variant Statistics"
                subLabel="GATK SNPs & InDels summary text file"
                icon="📄"
                onFile={setVariantsStats}
              />
            </div>
            <div className="field">
              <label>SnpEff Annotation Summaries (.html)</label>
              <DropArea
                accept=".html,.htm"
                isMulti={true}
                files={snpeffHtmls}
                mainLabel="Upload SnpEff HTML Files"
                subLabel="Per-sample SnpEff annotation summaries"
                icon="🌐"
                onFiles={setSnpeffHtmls}
              />
              {snpeffHtmls.length > 0 && (
                <div className="chip-list">
                  {snpeffHtmls.map((h, i) => (
                    <div key={i} className="chip">
                      <span>📄 {h.name}</span>
                      <button type="button" onClick={() => setSnpeffHtmls(snpeffHtmls.filter((_, idx) => idx !== i))}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section: Data Files & Deliverables (ZIP) – shown in Resources tab */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Data Files &amp; Deliverables (Resources Tab)</h3>
              <p>Upload a single ZIP containing only the important files you want listed in the Resources → Data Files &amp; Deliverables section of the report</p>
            </div>
          </div>

          <div className="info-banner">
            <div className="info-banner-icon">💡</div>
            <div className="info-banner-text">
              <strong>Tip:</strong> Put only final deliverables inside this ZIP (e.g. final VCF files, annotated variant tables, high-depth coverage plots, alignment statistics). Intermediate FastQC plots and raw MultiQC files will not appear automatically.
            </div>
          </div>

          <DropArea
            accept=".zip"
            file={resourcesZip}
            mainLabel="Upload Resources / Deliverables ZIP"
            subLabel="One ZIP file – contents will be extracted and listed in the report"
            icon="📦"
            onFile={setResourcesZip}
          />
        </div>

        {/* Section 5: Software & Tools Version Table */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Software &amp; Tools Version Table</h3>
              <p>Editable version numbers and software tools rendered in the Methodology section</p>
            </div>
          </div>
          <div className="sub-label">Software Tools Table</div>
          <table className="software-table">
            <thead>
              <tr>
                <th>Analysis Step / Feature</th>
                <th>Software Tool</th>
                <th>Version</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {softwareRows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input value={r.item} onChange={e => editSoftwareRow(i, "item", e.target.value)} placeholder="e.g. Alignment" />
                  </td>
                  <td>
                    <input value={r.software} onChange={e => editSoftwareRow(i, "software", e.target.value)} placeholder="e.g. BWA-MEM" />
                  </td>
                  <td>
                    <input value={r.version} onChange={e => editSoftwareRow(i, "version", e.target.value)} placeholder="e.g. v0.7.17" />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button type="button" className="btn-remove-step" onClick={() => removeSoftwareRow(i)} title="Remove row">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="btn-add-step" onClick={addSoftwareRow}>+ Add Analysis Tool</button>
        </div>

        {/* Section 6: Methodology Description */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Experimental &amp; Bioinformatics Methodology</h3>
              <p>Customize the workflow description and bioinformatics protocols included in the final report</p>
            </div>
          </div>
          <div className="sub-label">Methodology Text</div>
          <textarea
            className="method-area"
            value={methodologyText}
            onChange={e => setMethodologyText(e.target.value)}
          />
          <div className="method-meta">
            <span className="method-word-count">{wordCount} words | {methodologyText.length} characters</span>
            <button type="button" className="method-clear" onClick={() => setMethodologyText(DEFAULT_METHODOLOGY)}>Reset to Default</button>
          </div>
        </div>

        {/* Section 7: Custom Logo Upload */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Company / Institutional Logo (Optional)</h3>
              <p>Upload a custom high-resolution logo image to replace the default ONEOMICS branding</p>
            </div>
          </div>
          <div className="sub-label">Custom Report Logo</div>
          <DropArea
            accept="image/*"
            file={logoFile}
            mainLabel="Upload Custom Logo"
            subLabel="PNG, JPG, or SVG image (Default: ONEOMICS Logo)"
            icon="🏷️"
            onFile={setLogoFile}
          />
        </div>
      </div>

      {/* Fixed Bottom Submit Action Bar */}
      <div className="wes-submit-bar">
        {error && <div className="wes-submit-error">⚠️ {error}</div>}
        {success && downloadedReport && (
          <div className="wes-submit-success">
            ✓ Report Generated!
            <a
              href={downloadedReport.reportUrl}
              target="_blank"
              rel="noreferrer"
              style={{ marginLeft: 8, color: "#15803d", fontWeight: 700, textDecoration: "underline" }}
            >
              Open Report ↗
            </a>
          </div>
        )}
        <button
          type="button"
          className="wes-btn-submit"
          onClick={handleUpload}
          disabled={loading}
        >
          {loading ? (
            <>
              <span style={{ width: 14, height: 14, borderWidth: 2, borderStyle: "solid", borderColor: "rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "wes-spin .8s linear infinite" }} />
              Generating WES Report…
            </>
          ) : (
            <> Upload &amp; Generate Report</>
          )}
        </button>
      </div>
    </div>
  );
}
